import {
  autoGeneratedExternalCodes,
  authorizedRolesToUpdateSeller,
  litaAutoGeneratedChannels,
} from 'Utils/constants'

/**
 * Returns an updated quote object with a new seller if specific conditions are met.
 *
 * Conditions for updating the seller (SalesAgentId and SalesAgentFullName):
 * - The quote is being edited for the first time (IsEdited is false), AND
 * - The quote was auto-generated by either:
 *   - Chat Bot or Web Documents, and the current user has a role authorized
 *     to update the seller, OR
 *   - LITA Documents (QuoteHeader.Channel), in which case any role is
 *     allowed to update the seller.
 *
 * @param {Object} quote - The quote object being edited.
 * @param {Array} userRoles - Array of roles associated with the current user.
 * @param {Object} currentUser - The current user performing the edit.
 * @returns {Object} - The updated quote object, or the original if no update is needed.
 */
export function updateSellerIfApplicable(quote, userRoles, currentUser) {
  const ivendUserCode = quote.QuoteHeader.SalesAgentExternalCodes?.find(
    code => code.ExternalSystem?.toLowerCase() === 'ivend user'.toLowerCase()
  )?.ExternalCode

  const isAutoGenerated = ivendUserCode
    ? autoGeneratedExternalCodes.includes(ivendUserCode)
    : false
  const isLitaGenerated = litaAutoGeneratedChannels.includes(
    quote.QuoteHeader?.Channel
  )
  const isEdited = quote.QuoteHeader?.IsEdited
  const hasAuthorizedPermission = userRoles.some(role =>
    authorizedRolesToUpdateSeller.includes(role.RoleName)
  )

  const shouldUpdateSeller =
    !isEdited &&
    (isLitaGenerated || (isAutoGenerated && hasAuthorizedPermission))

  if (!isLitaGenerated && !shouldUpdateSeller) {
    return quote
  }

  return {
    ...quote,
    QuoteHeader: {
      ...quote.QuoteHeader,
      ...(shouldUpdateSeller && {
        SalesAgentId: currentUser.CustomerId,
        SalesAgentFullName: currentUser.FullName,
      }),
    },
  }
}
